<script>
import SignaturePad from "@/components/SignaturePad";
import { verifyToken, submitSignature, selectSigningOptions } from "@/helpers/services/documentSignature.service";

const LOGO_URL = "https://getbase.s3.ap-southeast-1.amazonaws.com/Techventis+Medic+Sdn+Bhd.png";
const COMPANY_NAME = "Techventis Medic Sdn Bhd";

export default {
  name: "ExternalSign",
  components: { SignaturePad },
  data() {
    return {
      logoUrl: LOGO_URL,
      companyName: COMPANY_NAME,
      loading: true,
      submitting: false,
      done: false,
      errorMessage: "",
      info: null,
      signatureData: "",
      signerName: "",
      // Customer's current pick per line index → option uuid. Mirrors the
      // server's `is_selected` and is the value posted with the signature.
      selections: {},
      selecting: false,
      selectionError: "",
    };
  },
  computed: {
    docTypeLabel() {
      const t = this.info?.doc_type || "";
      return ({
        quotation: "Quotation",
        salesorder: "Sales Order",
        salesinvoice: "Sales Invoice",
        deliveryorder: "Delivery Order",
        creditnote: "Credit Note",
        debitnote: "Debit Note",
        consignment: "Consignment",
        cashsale: "Cash Sale",
        deliveryreturn: "Delivery Return",
      }[t] || t);
    },
    signerLabel() {
      const t = this.info?.signer_type || "";
      return ({ customer: "Customer", received_by: "Receiver", issued_by: "Issuer", agent: "Sales Agent" }[t] || t);
    },
    expiresLabel() {
      if (!this.info?.expires_at) return "";
      try {
        return new Date(this.info.expires_at).toLocaleString();
      } catch (_) { return this.info.expires_at; }
    },
    isQuotation() {
      return (this.info?.doc_type || "").toLowerCase() === "quotation";
    },
    subtotalSum() {
      return (this.info?.document?.details || []).reduce((s, l) => s + (Number(l.sub_total) || 0), 0);
    },
    taxTotal() {
      const t = this.info?.document?.tax_total;
      if (t != null) return Number(t) || 0;
      return (this.info?.document?.details || []).reduce((s, l) => s + (Number(l.tax_amount) || 0), 0);
    },
    grandTotal() {
      // Prefer the AutoCount header total if it already includes tax;
      // otherwise compute = subtotal + tax.
      const headerTotal = Number(this.info?.document?.total || 0);
      if (headerTotal && this.taxTotal === 0) return headerTotal;
      return this.subtotalSum + this.taxTotal;
    },
    alreadySigned() {
      return !!this.info?.already_signed;
    },
    existingCustomerSig() {
      // If THIS token has been signed, prefer its data; otherwise show the
      // latest prior customer signature for this doc (e.g. signed on a previous link).
      if (this.info?.current_signature_data) {
        return {
          signature_data: this.info.current_signature_data,
          signer_name: this.info.signer_name,
          signed_at: this.info.current_signed_at,
        };
      }
      return this.info?.customer_signature || null;
    },
    priceBinding() {
      return this.info?.price_binding || null;
    },
    priceBindingUntil() {
      const v = this.info?.price_binding_until;
      return v ? String(v).substr(0, 10) : "";
    },
    docStatus() {
      return this.info?.document?.status || "";
    },
    /** Once the customer has signed, offers can no longer be changed. */
    optionsLocked() {
      return this.alreadySigned || !!this.existingCustomerSig || this.selecting;
    },
    hasAnyOptions() {
      return (this.info?.document?.details || []).some((l) => Array.isArray(l.options) && l.options.length);
    },
    priceBindingLabel() {
      return ({
        once: "Once-off (this document only)",
        always: "Always (every future order)",
        min_qty: "Min. Quantity (when minimum qty is met)",
      }[this.priceBinding] || "");
    },
    priceBindingDescription() {
      return ({
        once: "These prices apply to this quotation only. Future orders will use standard pricing.",
        always: "These prices will automatically apply to every future order for the items listed here.",
        min_qty: "These prices apply only when the order quantity meets the minimum specified per item.",
      }[this.priceBinding] || "");
    },
    canSubmit() {
      if (this.alreadySigned || this.existingCustomerSig) return false;
      return !!this.signatureData && !!(this.signerName || "").trim() && !this.submitting;
    },
  },
  async created() {
    try {
      const r = await verifyToken(this.$route.params.token);
      this.applyInfo(r?.data || null);
      if (this.info?.signer_name) this.signerName = this.info.signer_name;
    } catch (e) {
      this.errorMessage = e?.response?.data?.error || "This signing link is invalid or has expired.";
    } finally {
      this.loading = false;
    }
  },
  methods: {
    onSignatureChange(data) { this.signatureData = data; },
    /** Replace page data and re-sync the radio state from `is_selected`. */
    applyInfo(info) {
      this.info = info;
      const sel = {};
      (info?.document?.details || []).forEach((ln, i) => {
        const opts = Array.isArray(ln.options) ? ln.options : [];
        if (!opts.length) return;
        const picked = opts.find((o) => o.is_selected) || opts[0];
        sel[i] = picked ? picked.uuid : null;
      });
      this.selections = sel;
    },
    currentSelections() {
      const details = this.info?.document?.details || [];
      return Object.keys(this.selections)
        .map((i) => ({ item_code: details[i]?.item_code, option_uuid: this.selections[i] }))
        .filter((s) => s.item_code && s.option_uuid);
    },
    optionLabel(o) {
      const price = Number(o.unit_price || 0).toFixed(2);
      return `${o.qty} ${o.uom || ""} @ ${price} = ${this.formatMoney(o.sub_total)}`;
    },
    openImage(url) {
      if (url) window.open(url, "_blank", "noopener");
    },
    async onOptionChange(lineIndex, uuid) {
      if (this.optionsLocked || !uuid || this.selections[lineIndex] === uuid) return;
      const previous = this.selections[lineIndex];
      this.$set(this.selections, lineIndex, uuid);
      this.selecting = true;
      this.selectionError = "";
      try {
        const r = await selectSigningOptions(this.$route.params.token, this.currentSelections());
        if (r?.data) this.applyInfo(r.data);
      } catch (e) {
        // 409: the ledger refused the change — show why and put the radio back.
        this.selectionError = e?.response?.data?.error || this.$t("signing.selectionFailed");
        this.$set(this.selections, lineIndex, previous);
      } finally {
        this.selecting = false;
      }
    },
    formatMoney(v) {
      return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      try {
        await submitSignature(
          this.$route.params.token,
          this.signatureData,
          this.signerName.trim(),
          this.currentSelections()
        );
        this.done = true;
      } catch (e) {
        this.errorMessage = e?.response?.data?.error || "Failed to submit signature. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<template>
  <div class="sign-page">
    <!-- Top bar with logo + company name -->
    <header class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <img :src="logoUrl" :alt="companyName" class="brand-logo" />
          <div class="brand-text">
            <div class="brand-name">{{ companyName }}</div>
            <div class="brand-sub">Document Signing Portal</div>
          </div>
        </div>
        <div class="topbar-right">
          <i class="mdi mdi-shield-check-outline mr-1"></i>
          <span class="small">Secure Signing</span>
        </div>
      </div>
    </header>

    <main class="container py-4">
      <!-- Loading -->
      <div v-if="loading" class="state-card">
        <b-spinner variant="primary" />
        <div class="mt-3 text-muted">Loading document…</div>
      </div>

      <!-- Error -->
      <div v-else-if="errorMessage" class="state-card">
        <div class="icon-circle bg-danger-soft">
          <i class="mdi mdi-alert-circle-outline"></i>
        </div>
        <h4 class="mt-3 text-danger">{{ errorMessage }}</h4>
        <p class="text-muted">Please contact the sender for a new link.</p>
      </div>

      <!-- Success -->
      <div v-else-if="done" class="state-card">
        <div class="icon-circle bg-success-soft">
          <i class="mdi mdi-check-circle-outline"></i>
        </div>
        <h3 class="mt-3 text-success mb-2">Signature submitted</h3>
        <p class="text-muted">Thank you. You may close this page.</p>
      </div>

      <!-- Active signing flow -->
      <template v-else-if="info">
        <!-- Hero card: doc summary -->
        <section class="hero-card">
          <div class="hero-band">
            <span class="hero-doctype">{{ docTypeLabel }}</span>
            <span v-if="docStatus" class="hero-status-pill" :class="'status-' + docStatus">{{ docStatus }}</span>
            <span class="hero-signer-pill">
              <i class="mdi mdi-account-edit-outline mr-1"></i>{{ signerLabel }}
            </span>
          </div>
          <div class="hero-body">
            <div class="hero-docno">{{ info.doc_no }}</div>
            <div v-if="info.signer_name" class="text-muted mt-1">Signing as <strong>{{ info.signer_name }}</strong></div>
            <div v-if="expiresLabel" class="text-muted small mt-1">
              <i class="mdi mdi-clock-outline mr-1"></i>Link expires {{ expiresLabel }}
            </div>
          </div>
        </section>

        <!-- Document details -->
        <section v-if="info.document" class="doc-card">
          <div class="doc-header-grid">
            <div>
              <div class="kv-label">Customer</div>
              <div class="kv-value">{{ info.document.debtor_name || info.document.debtor_code }}</div>
              <div class="kv-sub">{{ info.document.debtor_code }}</div>
            </div>
            <div>
              <div class="kv-label">Date</div>
              <div class="kv-value">{{ info.document.doc_date || '—' }}</div>
            </div>
            <div class="text-md-right">
              <div class="kv-label">Total ({{ info.document.currency_code || 'MYR' }})</div>
              <div class="kv-total">{{ formatMoney(grandTotal) }}</div>
            </div>
          </div>

          <h6 class="doc-section-title">Line Items</h6>
          <b-alert v-if="selectionError" variant="danger" show dismissible class="small py-2" @dismissed="selectionError = ''">
            <i class="mdi mdi-alert-circle-outline mr-1"></i>{{ selectionError }}
          </b-alert>
          <p v-if="hasAnyOptions && !optionsLocked" class="text-muted small mb-2">
            <i class="mdi mdi-gesture-tap mr-1"></i>{{ $t('signing.chooseOption') }}
          </p>

          <div class="line-list">
            <div v-for="(ln, i) in info.document.details" :key="i" class="line-item">
              <div class="line-row">
                <!-- Product thumbnail: opens the full image in a new tab. -->
                <div class="line-thumb" :class="{ 'is-link': ln.product && ln.product.image_url }" @click="openImage(ln.product && ln.product.image_url)">
                  <img v-if="ln.product && ln.product.image_url" :src="ln.product.image_url" :alt="ln.product.name || ln.item_code" />
                  <i v-else class="mdi mdi-package-variant-closed"></i>
                </div>
                <div class="line-main">
                  <div class="line-code">{{ ln.item_code }}</div>
                  <div class="line-desc">{{ ln.description }}</div>
                  <div v-if="ln.product && (ln.product.name || ln.product.description)" class="line-product">
                    <div class="kv-label mb-0">{{ $t('signing.productDetails') }}</div>
                    <div v-if="ln.product.name" class="line-product-name">{{ ln.product.name }}</div>
                    <div v-if="ln.product.description" class="line-product-desc">{{ ln.product.description }}</div>
                  </div>
                </div>
                <div class="line-figures">
                  <div class="line-qty">{{ ln.qty }} <small class="text-muted">{{ ln.uom }}</small> × {{ Number(ln.unit_price).toFixed(2) }}</div>
                  <span v-if="ln.tax_code" class="tax-pill">{{ ln.tax_code }} <small>{{ Number(ln.tax_rate || 0).toFixed(0) }}%</small></span>
                  <div class="line-subtotal">{{ formatMoney(ln.sub_total) }}</div>
                </div>
              </div>

              <!-- Alternative offers: one radio per option; the current pick is pre-checked. -->
              <div v-if="ln.options && ln.options.length" class="line-options">
                <b-form-radio
                  v-for="opt in ln.options"
                  :key="opt.uuid"
                  :name="`line-opt-${i}`"
                  :value="opt.uuid"
                  :checked="selections[i]"
                  :disabled="optionsLocked"
                  class="line-option"
                  @change="(v) => onOptionChange(i, v)"
                >
                  <span class="line-option-label">{{ optionLabel(opt) }}</span>
                  <span v-if="opt.tax_code" class="tax-pill ml-1">{{ opt.tax_code }}</span>
                  <span v-if="opt.is_selected && opt.selected_by" class="selected-pill ml-1">
                    {{ opt.selected_by === 'customer' ? $t('quotation.selectedByCustomer') : $t('quotation.selectedByAgent') }}
                  </span>
                </b-form-radio>
              </div>
            </div>
          </div>

          <div class="totals">
            <div class="totals-row"><span class="text-muted">Subtotal</span><span>{{ formatMoney(subtotalSum) }}</span></div>
            <div v-if="taxTotal > 0" class="totals-row"><span class="text-muted">Tax</span><span>{{ formatMoney(taxTotal) }}</span></div>
            <div class="totals-row grand-row">
              <span>Total ({{ info.document.currency_code || 'MYR' }})</span>
              <span>{{ formatMoney(grandTotal) }}</span>
            </div>
          </div>
        </section>

        <!-- Pricing for Future Documents (quotation only) -->
        <section v-if="isQuotation && priceBinding" class="pricing-card">
          <div class="pricing-head">
            <div class="pricing-icon"><i class="mdi mdi-tag-multiple-outline"></i></div>
            <div>
              <div class="kv-label">Pricing for Future Documents</div>
              <div class="pricing-label">{{ priceBindingLabel }}</div>
            </div>
            <span class="pricing-pill" :class="'pill-' + priceBinding">
              {{ priceBinding === 'once' ? 'Once-off' : priceBinding === 'always' ? 'Always' : 'Min. Qty' }}
            </span>
          </div>
          <p class="pricing-desc">{{ priceBindingDescription }}</p>
          <p v-if="priceBindingUntil" class="pricing-until mb-0">
            <i class="mdi mdi-calendar-clock mr-1"></i>{{ $t('quotation.priceValidUntilX', [priceBindingUntil]) }}
          </p>
        </section>

        <!-- Signatures (Sales Agent + Your Signature side-by-side) -->
        <section class="sign-grid">
          <!-- Sales Agent (read-only, may be empty) -->
          <div class="sig-col agent-col">
            <div class="sig-col-head">
              <div class="sig-col-icon agent-icon"><i class="mdi mdi-account-tie-outline"></i></div>
              <div>
                <div class="sig-col-label">Sales Agent</div>
                <div v-if="info.agent_signature && info.agent_signature.signer_name" class="sig-col-name">{{ info.agent_signature.signer_name }}</div>
                <div v-else class="sig-col-name text-muted">—</div>
              </div>
              <span v-if="info.agent_signature" class="signed-pill ml-auto"><i class="mdi mdi-check"></i> Signed</span>
              <span v-else class="pending-pill ml-auto">Pending</span>
            </div>
            <div class="sig-display">
              <img v-if="info.agent_signature" :src="'data:image/png;base64,' + info.agent_signature.signature_data" alt="Sales Agent Signature" />
              <div v-else class="sig-display-empty">
                <i class="mdi mdi-pen-off"></i>
                <span class="small">Awaiting sales agent</span>
              </div>
            </div>
            <small v-if="info.agent_signature && info.agent_signature.signed_at" class="text-muted d-block mt-1">
              Signed {{ new Date(info.agent_signature.signed_at).toLocaleString() }}
            </small>
          </div>

          <!-- Customer (your) signature — interactive, or read-only if already signed -->
          <div class="sig-col customer-col">
            <div class="sig-col-head">
              <div class="sig-col-icon customer-icon"><i class="mdi mdi-fountain-pen-tip"></i></div>
              <div>
                <div class="sig-col-label">{{ signerLabel }} Signature</div>
                <div v-if="existingCustomerSig && existingCustomerSig.signer_name" class="sig-col-name">{{ existingCustomerSig.signer_name }}</div>
                <div v-else class="sig-col-name text-muted">{{ signerName || 'Enter your name below' }}</div>
              </div>
              <span v-if="existingCustomerSig" class="signed-pill ml-auto"><i class="mdi mdi-check"></i> Signed</span>
              <span v-else-if="signatureData" class="signed-pill ml-auto"><i class="mdi mdi-check"></i> Ready</span>
              <span v-else class="pending-pill ml-auto">Pending</span>
            </div>

            <!-- Customer already signed (this link OR a previous link) — read-only display. -->
            <template v-if="existingCustomerSig">
              <div class="sig-display">
                <img :src="'data:image/png;base64,' + existingCustomerSig.signature_data" alt="Customer Signature" />
              </div>
              <small v-if="existingCustomerSig.signed_at" class="text-muted d-block mt-1">
                Signed {{ new Date(existingCustomerSig.signed_at).toLocaleString() }}
              </small>
            </template>

            <!-- No prior signature — show pad + name input. -->
            <template v-else>
              <label class="name-label">Your Name <span class="text-danger">*</span></label>
              <b-form-input v-model="signerName" placeholder="e.g. John Tan" class="mb-2" />
              <SignaturePad ref="pad" :height="180" @change="onSignatureChange" />
            </template>
          </div>
        </section>

        <!-- Submit row — only when the customer hasn't signed yet. -->
        <div v-if="!existingCustomerSig" class="submit-row">
          <small class="text-muted"><i class="mdi mdi-lock-outline mr-1"></i>Your signature is transmitted securely.</small>
          <b-button variant="primary" size="lg" :disabled="!canSubmit" @click="submit">
            <b-spinner v-if="submitting" small class="mr-2" />
            <i v-else class="mdi mdi-send mr-2"></i>
            Submit Signature
          </b-button>
        </div>
        <div v-else class="submit-row submit-row-done">
          <small class="text-success"><i class="mdi mdi-check-circle mr-1"></i>This document has already been signed by the customer.</small>
        </div>
      </template>
    </main>

    <footer class="footer">
      <span>Powered by <strong>Techventis</strong></span>
    </footer>
  </div>
</template>

<style scoped>
.sign-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f4f0ff 0%, #eef2ff 50%, #f4f0ff 100%);
  display: flex;
  flex-direction: column;
}

/* Top bar */
.topbar {
  background: #fff;
  border-bottom: 1px solid #eaeaf2;
  box-shadow: 0 2px 6px rgba(123, 77, 255, 0.04);
  padding: 14px 0;
}
.topbar-inner {
  max-width: 920px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand-logo {
  height: 42px;
  width: auto;
  object-fit: contain;
}
.brand-text { line-height: 1.2; }
.brand-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2240;
  letter-spacing: 0.2px;
}
.brand-sub {
  font-size: 0.72rem;
  color: #7c4dff;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-weight: 600;
}
@media (max-width: 480px) {
  .brand-name { font-size: 0.88rem; }
  .brand-sub { font-size: 0.66rem; letter-spacing: 0.8px; }
  .brand-logo { height: 34px; }
}
.topbar-right {
  color: #7c4dff;
  font-weight: 500;
  display: flex;
  align-items: center;
}

/* Layout */
.container { max-width: 920px; }

/* State cards (loading/error/success) */
.state-card {
  background: #fff;
  border-radius: 14px;
  padding: 56px 24px;
  text-align: center;
  box-shadow: 0 4px 18px rgba(123, 77, 255, 0.06);
}
.icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.icon-circle i { font-size: 44px; }
.bg-success-soft { background: #e3f9ec; color: #1aa554; }
.bg-danger-soft { background: #fde7e9; color: #dc3545; }

/* Hero */
.hero-card {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 18px;
  box-shadow: 0 6px 24px rgba(123, 77, 255, 0.08);
}
.hero-band {
  background: linear-gradient(120deg, #7c4dff 0%, #5e35b1 100%);
  color: #fff;
  padding: 14px 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.hero-signer-pill {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
}
.hero-body { padding: 22px; }
.hero-docno {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.85rem;
  font-weight: 700;
  color: #1f2240;
  letter-spacing: 1px;
}

/* Doc detail card */
.doc-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  margin-bottom: 18px;
  box-shadow: 0 4px 18px rgba(123, 77, 255, 0.06);
}
.doc-header-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eef0f6;
  margin-bottom: 18px;
}
@media (max-width: 640px) {
  .doc-header-grid { grid-template-columns: 1fr; }
  .doc-header-grid > div.text-md-right { text-align: left !important; }
}
.kv-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #8a8fa8;
  margin-bottom: 4px;
}
.kv-value { font-weight: 600; color: #1f2240; }
.kv-sub { font-size: 0.85rem; color: #6c7290; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.kv-total { font-size: 1.6rem; font-weight: 700; color: #7c4dff; }

.doc-section-title {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #8a8fa8;
  margin-bottom: 10px;
}
.hero-status-pill {
  background: rgba(255, 255, 255, 0.9);
  color: #5e35b1;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  margin-left: 10px;
}
.hero-status-pill.status-won { color: #1aa554; }
.hero-status-pill.status-lost { color: #dc3545; }

/* Line items: stacked cards so they read well on a phone. */
.line-list { display: flex; flex-direction: column; gap: 10px; }
.line-item {
  border: 1px solid #eef0f6;
  border-radius: 10px;
  padding: 12px;
}
.line-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.line-thumb {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #f4f0ff;
  color: #b9a6ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  overflow: hidden;
}
.line-thumb.is-link { cursor: zoom-in; }
.line-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.line-main { flex: 1; min-width: 0; }
.line-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
  color: #7c4dff;
  font-weight: 600;
}
.line-desc { color: #1f2240; font-weight: 500; }
.line-product { margin-top: 6px; }
.line-product-name { font-size: 0.85rem; color: #1f2240; }
.line-product-desc { font-size: 0.8rem; color: #6c7290; white-space: pre-line; }
.line-figures {
  text-align: right;
  flex-shrink: 0;
  font-size: 0.85rem;
  color: #6c7290;
}
.line-subtotal { font-weight: 700; color: #1f2240; font-size: 1rem; margin-top: 2px; }
@media (max-width: 480px) {
  .line-row { flex-wrap: wrap; }
  .line-figures {
    width: 100%;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 10px;
    padding-left: 68px;
  }
  .line-subtotal { margin-left: auto; margin-top: 0; }
}
.line-options {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #d9d4ff;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.line-option { font-size: 0.9rem; }
.line-option-label { font-weight: 500; color: #1f2240; }
.selected-pill {
  display: inline-block;
  padding: 1px 8px;
  background: #e3f9ec;
  color: #1aa554;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}
.totals { margin-top: 14px; border-top: 1px solid #eef0f6; }
.totals-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 4px;
  font-size: 0.9rem;
}
.totals-row.grand-row {
  background: #f4f0ff;
  color: #7c4dff;
  font-weight: 700;
  font-size: 1rem;
  border-top: 2px solid #7c4dff;
  border-radius: 0 0 8px 8px;
  padding: 10px 8px;
}
.pricing-until {
  margin-top: 8px;
  color: #b8860b;
  font-size: 0.85rem;
  font-weight: 600;
}
.tax-pill {
  display: inline-block;
  padding: 2px 8px;
  background: #f4f0ff;
  color: #7c4dff;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}
.tax-pill small { opacity: 0.75; margin-left: 3px; }

/* Pricing for Future Documents card (quotation only) */
.pricing-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px 22px;
  margin-bottom: 18px;
  box-shadow: 0 4px 18px rgba(123, 77, 255, 0.06);
  border-left: 4px solid #7c4dff;
}
.pricing-head {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pricing-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f4f0ff;
  color: #7c4dff;
  font-size: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pricing-label {
  font-weight: 600;
  color: #1f2240;
  font-size: 1rem;
}
.pricing-pill {
  margin-left: auto;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
}
.pill-once { background: #f0f1f7; color: #6c7290; }
.pill-always { background: #f4f0ff; color: #7c4dff; }
.pill-min_qty { background: #fff3cd; color: #b8860b; }
.pricing-desc {
  margin: 12px 0 0;
  color: #6c7290;
  font-size: 0.88rem;
  line-height: 1.5;
}

/* Signatures side-by-side grid */
.sign-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 18px;
}
@media (max-width: 720px) {
  .sign-grid { grid-template-columns: 1fr; }
}
.sig-col {
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 4px 18px rgba(123, 77, 255, 0.06);
}
.agent-col { border-top: 4px solid #7c4dff; }
.customer-col { border-top: 4px solid #1aa554; }
.sig-col-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.sig-col-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}
.agent-icon { color: #7c4dff; background: #f4f0ff; }
.customer-icon { color: #1aa554; background: #e3f9ec; }
.sig-col-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #8a8fa8;
}
.sig-col-name {
  font-weight: 600;
  color: #1f2240;
  font-size: 0.92rem;
}
.signed-pill {
  background: #e3f9ec;
  color: #1aa554;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}
.pending-pill {
  background: #f0f1f7;
  color: #6c7290;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}
.sig-display {
  background: #fafafa;
  border: 1px dashed #d9d4ff;
  border-radius: 8px;
  padding: 10px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sig-display img {
  display: block;
  max-width: 100%;
  max-height: 160px;
  object-fit: contain;
}
.sig-display-empty {
  text-align: center;
  color: #b0b5cc;
}
.sig-display-empty i { font-size: 36px; display: block; margin-bottom: 6px; }
.name-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f2240;
  margin-bottom: 4px;
}
.submit-row {
  background: #fff;
  border-radius: 14px;
  padding: 16px 22px;
  margin-bottom: 22px;
  box-shadow: 0 4px 18px rgba(123, 77, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

/* Footer */
.footer {
  text-align: center;
  padding: 16px 0 24px;
  color: #8a8fa8;
  font-size: 0.8rem;
}
</style>
